import { useState, useEffect, useCallback } from 'react';
import { NotificationAPI, Notification } from '../api/notifications';
import { useSocket } from './useSocket';

interface UseNotificationsOptions {
  pollingInterval?: number;
  enableRealTime?: boolean;
  autoMarkAsRead?: boolean;
  enabled?: boolean;
}

interface UseNotificationsReturn {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  markAsRead: (id: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: number) => Promise<void>;
  refreshNotifications: () => Promise<void>;
}

export function useNotifications(options: UseNotificationsOptions = {}): UseNotificationsReturn {
  const {
    pollingInterval = 30000, 
    enableRealTime = true,
    autoMarkAsRead = false,
    enabled = true
  } = options;

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const socket = useSocket();

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [notificationsResponse, unreadCountResponse] = await Promise.all([
        NotificationAPI.getAll({ perPage: 50, skip: 0 }),
        NotificationAPI.getUnreadCount()
      ]);

      if (notificationsResponse.code === '200') {
        const allNotifications = notificationsResponse.data || [];
        setNotifications(allNotifications as any);

      
        const unreadNotifications = (allNotifications as any).filter((n: any) => !n.read);
        setUnreadCount(unreadNotifications.length);
      }

      if (unreadCountResponse.code === '200' && unreadCountResponse.data?.count !== undefined) {
        setUnreadCount(prevCount => {
          const apiCount = unreadCountResponse.data.count;
          return apiCount > 0 ? apiCount : prevCount;
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  const markAsRead = useCallback(async (id: number) => {
    try {
      const response = await NotificationAPI.markAsRead(id);
      if (response.code === '200') {
        setNotifications(prev => 
          prev.map(notif => 
            notif.id === id ? { ...notif, read: true } : notif
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mark as read');
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      const response = await NotificationAPI.markAllAsRead();
      if (response.code === '200') {
        setNotifications(prev => 
          prev.map(notif => ({ ...notif, read: true }))
        );
        setUnreadCount(0);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mark all as read');
    }
  }, []);

  const deleteNotification = useCallback(async (id: number) => {
    try {
      const response = await NotificationAPI.delete(id);
      if (response.code === '200') {
        const notificationToDelete = notifications.find(n => n.id === id);
        setNotifications(prev => prev.filter(notif => notif.id !== id));

        if (notificationToDelete && !notificationToDelete.read) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete notification');
    }
  }, [notifications]);

  const refreshNotifications = useCallback(async () => {
    await fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    if (!enabled) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      setError(null);
      return;
    }

    fetchNotifications();
  }, [fetchNotifications, enabled]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (!enableRealTime && pollingInterval > 0) {
      const interval = setInterval(fetchNotifications, pollingInterval);
      return () => clearInterval(interval);
    }
  }, [fetchNotifications, enableRealTime, pollingInterval, enabled]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (enableRealTime && socket) {
      const handleNewNotification = (notification: Notification) => {
        setNotifications(prev => [notification, ...prev]);
        if (!notification.read) {
          setUnreadCount(prev => prev + 1);
        }
      };

      const handleNotificationUpdate = (updatedNotification: Notification) => {
        setNotifications(prev => 
          prev.map(notif => 
            notif.id === updatedNotification.id ? updatedNotification : notif
          )
        );
      };

      const handleNotificationDelete = ({ id }: { id: number }) => {
        const notificationToDelete = notifications.find(n => n.id === id);
        setNotifications(prev => prev.filter(notif => notif.id !== id));

        if (notificationToDelete && !notificationToDelete.read) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
      };

      socket.on('newNotification', handleNewNotification);
      socket.on('notificationUpdated', handleNotificationUpdate);
      socket.on('notificationDeleted', handleNotificationDelete);

      return () => {
        socket.off('newNotification', handleNewNotification);
        socket.off('notificationUpdated', handleNotificationUpdate);
        socket.off('notificationDeleted', handleNotificationDelete);
      };
    }
  }, [enableRealTime, socket, notifications, enabled]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (autoMarkAsRead && notifications.length > 0) {
      const unreadNotifications = notifications.filter(n => !n.read);
      if (unreadNotifications.length > 0) {
        const timer = setTimeout(() => {
          markAsRead(unreadNotifications[0].id);
        }, 2000);

        return () => clearTimeout(timer);
      }
    }
  }, [notifications, autoMarkAsRead, markAsRead, enabled]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refreshNotifications
  };
}
